import { useEffect, ReactElement, useRef } from "react";
import { useForm } from "react-hook-form";
import { t } from "@lingui/core/macro";
import { useLocation, useQueryParams } from "_/components/router";

import { FormTextField } from "_/components/text-field";
import { Button } from "_/components/button";
import { H3 } from "_/components/typography";
import { useAddToast } from "_/components/toasts";
import { Form } from "_/components/form";

import { User } from "_/data/users";

import { api, ApiResponse } from "_/utils";
import { routeUrls } from "_/routes";

import * as S from "./styled";

export const AccountRsvp = (): ReactElement => {
  const [_location, setLocation] = useLocation();
  const { control, handleSubmit } = useForm<{
    name: string;
    password: string;
    password1: string;
  }>();
  const addToast = useAddToast();
  const queryParams = useQueryParams();
  const redirectedRef = useRef(false);

  useEffect(() => {
    // Only redirect and toast once.
    if (redirectedRef.current) {
      return;
    }

    if (!queryParams.token || !queryParams.id) {
      addToast({
        kind: "danger",
        title: t`components.account-rsvp.error`,
        content: t`components.account-rsvp.missing-query-params`,
      });

      setLocation(routeUrls.public.login);
      redirectedRef.current = true;
    }
  }, [addToast, queryParams, setLocation]);

  const onSubmit = handleSubmit(async (data) => {
    const rsvpData = {
      invitationId: queryParams.id,
      token: queryParams.token,
      name: data.name,
      password: data.password,
    };

    const resp = await api.post<ApiResponse<User>>(
      `/invitations/${queryParams.id}/rsvp`,
      rsvpData
    );
    const user = resp.data.data;

    addToast({
      kind: "success",
      title: t`common.success`,
      content: t`components.account-rsvp.welcome-message ${user.name}`,
      timeout: 5000,
    });

    setLocation(routeUrls.public.login);
  });

  return (
    <S.Wrapper>
      <H3>{t`components.account-rsvp.title`}</H3>
      <Form onSubmit={onSubmit}>
        <FormTextField
          control={control}
          name="name"
          rules={{ required: true }}
          label={t`common.name`}
        />
        <FormTextField
          control={control}
          name="password"
          rules={{ required: t`common.password-required` }}
          label={t`common.password`}
          type="new-password"
        />
        <FormTextField
          control={control}
          name="password1"
          rules={{
            required: t`common.password-required`,
            validate: (value, formValues) => {
              return (
                value === formValues.password || t`common.password-mismatch`
              );
            },
          }}
          label={t`components.account-rsvp.password-confirmation`}
          type="password"
        />
        <Button type="submit">{t`common.continue`}</Button>
      </Form>
    </S.Wrapper>
  );
};
